import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, Clock, ClipboardList, UserCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bar = ({ pct, color }) => (
  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mt-1">
    <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

const Metric = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-400 font-medium">{label}</div>
    <div className="text-sm font-bold text-slate-800">{value}</div>
  </div>
);

const ATT_COLORS = { DM: 'bg-indigo-500', ADM: 'bg-teal-500', CP: 'bg-rose-500' };

export const CMDMohineePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/receptionist/cmd-overview-mohinee`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm" data-testid="cmd-mohinee-loading">Loading Reception 2 summary…</div>;
  if (!data) return null;

  const { officeVisits, attendance, reportIndex } = data;

  return (
    <Card className="border-0 shadow-md" data-testid="cmd-mohinee-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2">
          <Users className="w-4 h-4 text-cyan-600" />
          Reception 2 Snapshot — Nayana Mahajan (%-wise)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Office Visits */}
          <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-100" data-testid="cmd-mohinee-visits">
            <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-cyan-600" /><span className="font-bold text-sm text-cyan-900">Office Visits</span></div>
            <div className="grid grid-cols-2 gap-2">
              <Metric label="Total Visits" value={(officeVisits.total || 0).toLocaleString()} />
              <Metric label="Active DMs" value={officeVisits.uniqueDMs} />
              <Metric label="Top CP" value={officeVisits.topCP} />
              <Metric label="Top CP Share" value={`${officeVisits.topCPShare}%`} />
            </div>
          </div>

          {/* Attendance present % (July 26) */}
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="cmd-mohinee-attendance">
            <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-slate-600" /><span className="font-bold text-sm text-slate-800">Attendance — Present % (Jul 26)</span></div>
            {['DM', 'ADM', 'CP'].map(k => {
              const a = attendance?.[k] || { presentPct: 0, present: 0, absent: 0, marked: 0 };
              return (
                <div key={k} className="mb-2" data-testid={`cmd-mohinee-att-${k.toLowerCase()}`}>
                  <div className="text-[11px] font-semibold text-slate-700 flex justify-between">
                    <span>{k} ({a.marked} staff)</span>
                    <span>{a.presentPct}% · {a.present}P/{a.absent}A</span>
                  </div>
                  <Bar pct={a.presentPct} color={ATT_COLORS[k]} />
                </div>
              );
            })}
          </div>

          {/* Report Index completion */}
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="cmd-mohinee-reportindex">
            <div className="flex items-center gap-2 mb-2"><ClipboardList className="w-4 h-4 text-emerald-600" /><span className="font-bold text-sm text-emerald-900">Daily Work Report</span></div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Metric label="Total Items" value={reportIndex.total} />
              <Metric label="Completed" value={reportIndex.done} />
            </div>
            <div className="text-[11px] font-semibold text-emerald-800 flex justify-between items-center">
              <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" /> Completion</span>
              <span>{reportIndex.donePct}%</span>
            </div>
            <Bar pct={reportIndex.donePct} color="bg-emerald-500" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CMDMohineePanel;
